import { COUNTRIES_DATA } from '@fe/shared/Countries';
import { CSSProperties, Dispatch, ReactNode, RefObject, SetStateAction } from 'react';
import { T_FalsyValue } from 'src/common/client/types';
import { E_ButtonSize } from 'src/common/client/enums';
import { E_CountryDialCode } from '../../backend/enums';

// ! NOTE: This type replaces the following type: CountryIsoCodeMappingType
export type T_CountryIsoCodeMappingType = {
  [key: string]: {
    name: string;
    dialCode: string;
    isoCode: string;
    // one entry (IL) intentionally ships without a flag
    flag?: string;
  };
};

export const countryCodeMapping: Partial<T_CountryIsoCodeMappingType> = COUNTRIES_DATA.reduce<
  Partial<T_CountryIsoCodeMappingType>
>(
  (prevValue: Partial<T_CountryIsoCodeMappingType>, country) => {
    prevValue[country.dialCode] = { ...country };
    return prevValue;
  },
  {} as Partial<T_CountryIsoCodeMappingType>, // explicitly cast the initial value as Partial<CountryIsoCodeMappingType>
);

export type T_FlagIconProps = {
  countryAcronym: string;
  width?: string;
  height?: string;
};

export type T_CountryItem = {
  isoCode: string;
  dialCode: E_CountryDialCode;
  name: string;
};

export type T_CountryCodeSelectorContextProps = {
  filteredCodes: T_CountryItem[] | null | undefined;
  setFilteredCodes: (value: T_CountryItem[] | null | undefined) => void;
  selectedFlagCode?: string;
  setSelectedFlagCode: (code: string | undefined) => void;
  selectedCountryCode?: E_CountryDialCode;
  setSelectedCountryCode: (code: E_CountryDialCode | undefined) => void;
  countryCodeToShow: string;
  flagCodeToShow: string;
  inputRef: RefObject<HTMLInputElement>;
  focusOnSearchInput: (ms?: number) => void;
  setSearch: (val: string) => void;
  handleSelect: (country: T_CountryItem) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  selectedCountry: T_CountryItem | undefined;
  setSelectedCountry: Dispatch<SetStateAction<T_CountryItem | undefined>>;
};

export type T_FiltrationLogicProps = {
  setSelectedFlagCode: (code: string | undefined) => void;
  setSelectedCountryCode: (code: E_CountryDialCode | undefined) => void;
  setFilteredCodes: (codes: T_CountryItem[] | null | undefined) => void;
  setSelectedCountry: (code: T_CountryItem) => void;
};

export type T_LocalizedCountry = {
  id: number;
  alpha2: string;
  alpha3: string;
  en: string;
  ar: string;
  [lang: string]: string | number;
};

export type T_CountryCodeMenuProps = {
  children: ReactNode;
};

export type T_CustomCodeSelectorProps = {
  isCustomerCountryCode?: boolean;
  isRecipientCountryCode?: boolean;
};

export type T_CountryCodeSelectorProps = {
  initialCountryCode?: T_FalsyValue<E_CountryDialCode>;
  onChange?: (selectedCountryCode: E_CountryDialCode) => void | undefined;
  isWithoutInlineContext?: boolean;
} & T_CountryCodeMenuButtonProps &
  T_CustomCodeSelectorProps;

export type T_CountryCodeMenuButtonProps = {
  size?: E_ButtonSize;
  disabled?: boolean;
  menuButtonProps?: CSSProperties;
} & T_CustomCodeSelectorProps;

export type T_CountryCodeItemProps = {
  country: T_CountryItem;
} & T_CustomCodeSelectorProps;

export type T_CountryCodeSelectorProviderProps = {
  initialCountryCode?: T_FalsyValue<E_CountryDialCode | undefined>;
  onChange?: (selected: E_CountryDialCode) => void;
  children: any;
} & T_CustomCodeSelectorProps;
